import { motion } from 'motion/react';
import { ArrowLeft, Calendar, Clock, User, Tag, ArrowRight, Share2, BookOpen } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import type { CaseStudyData } from '../types/case-study';

interface CaseStudyTemplateProps {
  caseStudy: CaseStudyData;
  allCaseStudies?: CaseStudyData[];
}

export function CaseStudyTemplate({ caseStudy, allCaseStudies = [] }: CaseStudyTemplateProps) {
  const navigate = useNavigate();
  
  const relatedStudies = allCaseStudies.filter(study => 
    study.id !== caseStudy.id && 
    (study.category === caseStudy.category || 
     study.tags.some(tag => caseStudy.tags.includes(tag)))
  ).slice(0, 3);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: caseStudy.title,
        text: caseStudy.summary,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const renderContent = (section: CaseStudyData['content']['sections'][0]) => {
    switch (section.type) {
      case 'list':
        return (
          <ul className="space-y-3">
            {(section.content as string[]).map((item, index) => (
              <li key={index} className="flex items-start">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />
                <span className="text-muted-foreground leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'numbered-list':
        return (
          <ol className="space-y-4">
            {(section.content as string[]).map((item, index) => (
              <li key={index} className="flex items-start">
                <div className="w-8 h-8 bg-primary/20 text-primary rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0 border border-primary/30">
                  <span className="text-sm font-semibold">{index + 1}</span>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{item.split(':')[0]}</h4>
                  <p className="text-muted-foreground leading-relaxed">{item.split(':').slice(1).join(':')}</p>
                </div>
              </li>
            ))}
          </ol>
        );
      case 'quote':
        return (
          <blockquote className="border-l-4 border-primary pl-6 py-4 glass-strong rounded-r-lg">
            <p className="text-lg italic text-foreground leading-relaxed">{section.content as string}</p>
          </blockquote>
        );
      default:
        return (
          <div className="prose prose-invert max-w-none">
            {Array.isArray(section.content) ? (
              section.content.map((paragraph, index) => (
                <p key={index} className="text-muted-foreground leading-relaxed mb-4 last:mb-0">
                  {paragraph}
                </p>
              ))
            ) : (
              <p className="text-muted-foreground leading-relaxed">{section.content}</p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="pt-20 min-h-screen">
      {/* Header */}
      <section className="py-8 border-b border-border/50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Button
              variant="ghost"
              onClick={() => navigate('/case-studies')}
              className="mb-6 hover:bg-primary/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Case Studies
            </Button>
            
            <div className="max-w-4xl">
              <Badge variant="secondary" className="mb-4">
                {caseStudy.category}
              </Badge>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                {caseStudy.title}
              </h1>
              
              {caseStudy.subtitle && (
                <p className="text-xl text-muted-foreground mb-6">
                  {caseStudy.subtitle}
                </p>
              )}
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  {caseStudy.author}
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  {caseStudy.publishDate}
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  {caseStudy.readTime}
                </div>
                <Button variant="ghost" size="sm" onClick={handleShare}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* Sidebar */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="sticky top-24 space-y-6 h-fit">
              {/* Table of Contents */}
              <Card className="glass p-6">
                <h3 className="font-semibold mb-4 flex items-center">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Contents
                </h3>
                <nav className="space-y-2">
                  {caseStudy.content.sections.map((section, index) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="block text-sm text-muted-foreground hover:text-primary transition-colors duration-200 py-1"
                    >
                      {index + 1}. {section.title}
                    </a>
                  ))}
                </nav>
              </Card>

              {/* Tags */}
              <Card className="glass p-6">
                <h3 className="font-semibold mb-4 flex items-center">
                  <Tag className="w-4 h-4 mr-2" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {caseStudy.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </Card>

              {/* Featured Image */}
              <Card className="glass overflow-hidden">
                <ImageWithFallback
                  src={caseStudy.featuredImage}
                  alt={caseStudy.title}
                  className="w-full h-48 object-cover"
                />
              </Card>
            </div>
          </motion.aside>

          {/* Main Content */}
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3"
          >
            {/* Summary */}
            <Card className="glass p-8 mb-8">
              <h2 className="text-2xl font-semibold mb-4">Executive Summary</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {caseStudy.summary}
              </p>
            </Card>

            {/* Content Sections */}
            <div className="space-y-12">
              {caseStudy.content.sections.map((section, index) => (
                <motion.section
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="scroll-mt-24"
                >
                  <h2 className="text-3xl font-semibold mb-6">{section.title}</h2>
                  {renderContent(section)}
                </motion.section>
              ))}
            </div>

            {/* Related Studies */}
            {relatedStudies.length > 0 && (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="mt-16 pt-12 border-t border-border/50"
              >
                <h2 className="text-3xl font-semibold mb-8">Related Studies</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedStudies.map((study) => (
                    <Card key={study.id} className="glass overflow-hidden hover:border-primary/50 transition-all duration-300">
                      <ImageWithFallback
                        src={study.featuredImage}
                        alt={study.title}
                        className="w-full h-32 object-cover"
                      />
                      <div className="p-4">
                        <Badge variant="secondary" className="mb-2 text-xs">
                          {study.category}
                        </Badge>
                        <h3 className="font-semibold mb-2 line-clamp-2">{study.title}</h3>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {study.summary}
                        </p>
                        <Link
                          to={`/case-studies/${study.id}`}
                          className="text-primary hover:text-primary/80 text-sm font-medium inline-flex items-center"
                        >
                          Read More
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Link>
                      </div>
                    </Card>
                  ))}
                </div>
              </motion.section>
            )}
          </motion.main>
        </div>
      </div>
    </div>
  );
}